import { useIntl } from 'react-intl';

import { LinkButton } from '@atlaskit/button/new';
import { Stack } from '@atlaskit/primitives';

import { settings } from 'src/settings';
import { urls } from 'src/urls/global';

import {
  AtlassianIntelligencePreference,
  AtlassianIntelligencePreferenceOnToggleListener,
  PreferenceBox,
  PreferenceHeader,
  PreferenceDescription,
} from './atlassian-intelligence-preference';
import { messages } from './atlassian-intelligence.i18n';
import atlassianIntelligenceUrls from './urls';

export type AtlassianIntelligencePreferencesScreenProps = {
  isRovoChatInitiallyChecked: boolean;
  onRovoChatToggle?: AtlassianIntelligencePreferenceOnToggleListener;
  isAiWritingAssistantInitiallyChecked: boolean;
  onAiWritingAssistantToggle?: AtlassianIntelligencePreferenceOnToggleListener;
  isAgenticPipelinesInitiallyChecked: boolean;
  onAgenticPipelinesToggle?: AtlassianIntelligencePreferenceOnToggleListener;
  isAgenticStepsEnabled: boolean;
  isAgenticWorkspaceToggleEnabled: boolean;
};

export function AtlassianIntelligencePreferencesScreen({
  isRovoChatInitiallyChecked,
  onRovoChatToggle,
  isAiWritingAssistantInitiallyChecked,
  onAiWritingAssistantToggle: onAiWritingAssistantToggle,
  isAgenticPipelinesInitiallyChecked,
  onAgenticPipelinesToggle,
  isAgenticStepsEnabled,
  isAgenticWorkspaceToggleEnabled,
}: AtlassianIntelligencePreferencesScreenProps) {
  const { formatMessage } = useIntl();

  return (
    <Stack space="space.200">
      <AtlassianIntelligencePreference
        header={messages.atlassianIntelligenceRovoChatPreferenceHeader}
        subHeader={{
          message: messages.atlassianIntelligenceRovoChatPreferenceSubHeader,
          link: {
            href: settings().ADMINHUB_BASE_URL,
            ...messages.atlassianIntelligenceRovoChatPreferenceSubHeaderAtlassianAdmin,
          },
        }}
        description={{
          message: messages.atlassianIntelligenceRovoChatPreferenceDescription,
          link: {
            href: urls.external.aboutRovo,
            ...messages.atlassianIntelligenceRovoChatPreferenceDescriptionLearnMore,
          },
        }}
        lozenges={[
          {
            lozenge: {
              appearance: 'new',
            },
            message: messages.atlassianIntelligenceRovoChatPreferenceLozenge,
          },
        ]}
        onToggleChecked={onRovoChatToggle}
        isInitiallyChecked={isRovoChatInitiallyChecked}
        testId="rovo-chat-preference"
      />

      <AtlassianIntelligencePreference
        header={
          messages.atlassianIntelligenceAiWritingAssistantPreferenceHeader
        }
        description={{
          message:
            messages.atlassianIntelligenceAiWritingAssistantPreferenceDescription,
          link: {
            href: atlassianIntelligenceUrls.ui.whatIsAtlassianIntelligence(),
            ...messages.atlassianIntelligenceAiWritingAssistantPreferenceDescriptionLearnMore,
          },
        }}
        onToggleChecked={onAiWritingAssistantToggle}
        isInitiallyChecked={isAiWritingAssistantInitiallyChecked}
        testId="ai-writing-assistant-preference"
      />

      {isAgenticWorkspaceToggleEnabled && isAgenticStepsEnabled && (
        <AtlassianIntelligencePreference
          header={
            messages.atlassianIntelligenceAgenticPipelinesPreferenceHeader
          }
          description={{
            message:
              messages.atlassianIntelligenceAgenticPipelinesPreferenceDescription,
          }}
          lozenges={[
            {
              lozenge: { appearance: 'new' },
              message: messages.atlassianIntelligenceRovoChatPreferenceLozenge,
            },
          ]}
          onToggleChecked={onAgenticPipelinesToggle}
          isInitiallyChecked={isAgenticPipelinesInitiallyChecked}
          isToggleDisabled={!isAgenticStepsEnabled}
          testId="agentic-pipelines-preference"
        />
      )}

      {isAgenticWorkspaceToggleEnabled && !isAgenticStepsEnabled && (
        <PreferenceBox>
          <Stack space="space.100">
            <PreferenceHeader
              header={
                messages.atlassianIntelligenceAgenticPipelinesPreferenceHeader
              }
              lozenges={[
                {
                  lozenge: { appearance: 'new' },
                  message:
                    messages.atlassianIntelligenceRovoChatPreferenceLozenge,
                },
              ]}
            />
            <PreferenceDescription
              description={{
                message:
                  messages.atlassianIntelligenceAgenticPipelinesPreferenceDescription,
              }}
            />
          </Stack>
          <LinkButton
            href={atlassianIntelligenceUrls.ui.agenticPipelinesBetaRegistration()}
            target="_new"
          >
            {formatMessage(
              messages.atlassianIntelligenceAgenticPipelinesRegisterButton
            )}
          </LinkButton>
        </PreferenceBox>
      )}
    </Stack>
  );
}
